import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, addDoc, query, where, onSnapshot, getDocs, serverTimestamp, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import emailjs from '@emailjs/browser';
import '../styles/Contacts.css';
import { IoChevronBackOutline, IoPersonOutline, IoLogOutOutline } from 'react-icons/io5';

const Contacts = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [contacts, setContacts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init("MQPu98ZKG8Vt4PrOI");
  }, []);

  useEffect(() => {
    if (currentUser) {
      // Listen for both contacts where user is sender or receiver
      const fromQuery = query(
        collection(db, 'contacts'),
        where('from', '==', currentUser.email)
      );

      const toQuery = query(
        collection(db, 'contacts'),
        where('to', '==', currentUser.email)
      );

      const unsubscribeFrom = onSnapshot(fromQuery, (fromSnapshot) => {
        const fromContacts = fromSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          isReceived: false
        }));

        const unsubscribeTo = onSnapshot(toQuery, (toSnapshot) => {
          const toContacts = toSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            isReceived: true
          }));

          // Combine and deduplicate contacts
          const allContacts = [...fromContacts, ...toContacts];
          const uniqueContacts = allContacts.filter((contact, index, self) =>
            index === self.findIndex((c) => 
              (c.from === contact.from && c.to === contact.to) ||
              (c.from === contact.to && c.to === contact.from)
            )
          );

          setContacts(uniqueContacts);
          setLoading(false);
        });

        return () => unsubscribeTo();
      });

      return () => unsubscribeFrom();
    }
  }, [currentUser]);

  const getContactDisplayInfo = (contact) => {
    const isReceived = contact.isReceived;
    const otherUserEmail = isReceived ? contact.from : contact.to;
    const displayName = otherUserEmail.split('@')[0];
    
    return {
      email: otherUserEmail,
      displayName,
      status: contact.status,
      online: contact.online,
      muted: contact.muted,
      blocked: contact.blocked,
      isReceived
    };
  };

  const handleBack = () => {
    navigate('/chat');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      setError('Failed to log out');
    }
  };

  const handleInvite = async () => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    if (email === currentUser.email) {
      setError('You cannot invite yourself');
      return;
    }

    setError('');
    setSuccess('');

    try {
      // Check if contact already exists
      const contactsRef = collection(db, 'contacts');
      const q = query(
        contactsRef,
        where('from', '==', currentUser.email),
        where('to', '==', email)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setError('Contact already exists');
        return;
      }

      // Send email invitation first
      const templateParams = {
        to_name: email.split('@')[0],
        to_email: email,
        from_name: currentUser.displayName || currentUser.email.split('@')[0],
        chat_link: `https://realtimechatapp-57e81.web.app/signup?invite=true&from=${currentUser.email}`,
        reply_to: currentUser.email
      };

      console.log('Sending email with params:', templateParams);

      try {
        const emailResponse = await emailjs.send(
          "service_6z6au0p",
          "template_30cjl0b",
          templateParams,
          "MQPu98ZKG8Vt4PrOI"
        );

        console.log('Email sent successfully:', emailResponse);

        if (emailResponse.status !== 200) {
          throw new Error('Failed to send invitation email');
        }

        // Only add contact to Firestore if email was sent successfully
        await addDoc(collection(db, 'contacts'), {
          from: currentUser.email,
          to: email,
          status: 'pending',
          createdAt: serverTimestamp(),
          online: false,
          lastSeen: null,
          blocked: false,
          muted: false
        });

        setSuccess('Invitation sent successfully');
        setShowInviteModal(false);
        setEmail('');
      } catch (emailError) {
        console.error('EmailJS error:', emailError);
        throw new Error('Failed to send invitation email: ' + emailError.message);
      }
    } catch (error) {
      console.error('Invitation error:', error);
      setError(error.message || 'Failed to send invitation. Please try again.');
    }
  };

  const handleInviteClick = () => {
    setShowInviteModal(true);
  };

  const getInitials = (email, displayName) => {
    if (displayName) {
      return displayName.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email[0].toUpperCase();
  };

  const filteredContacts = contacts.filter(contact =>
    contact.to.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAccountClick = () => {
    // Navigate to account settings page when implemented
    setIsDropdownOpen(false);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await deleteDoc(doc(db, 'contacts', contactId));
      setActiveDropdown(null);
    } catch (error) {
      setError('Failed to delete contact');
    }
  };

  const handleMuteContact = async (contactId) => {
    try {
      await updateDoc(doc(db, 'contacts', contactId), {
        muted: true
      });
      setActiveDropdown(null);
    } catch (error) {
      setError('Failed to mute contact');
    }
  };

  const handleBlockContact = async (contactId) => {
    try {
      await updateDoc(doc(db, 'contacts', contactId), {
        blocked: true
      });
      setActiveDropdown(null);
    } catch (error) {
      setError('Failed to block contact');
    }
  };

  const handleChat = async (contact) => {
    if (contact.status !== 'active') return;

    try {
      let chatRoomId = contact.chatRoomId;
      const otherUserEmail = contact.isReceived ? contact.from : contact.to;
      
      if (!chatRoomId) {
        // Create a new chat room if it doesn't exist
        const chatRoomRef = await addDoc(collection(db, 'chatRooms'), {
          participants: [currentUser.email, otherUserEmail],
          createdAt: serverTimestamp(),
          lastMessage: null,
          lastMessageTime: null
        });
        
        chatRoomId = chatRoomRef.id;
        
        // Update both contacts with the chat room ID
        await updateDoc(doc(db, 'contacts', contact.id), {
          chatRoomId: chatRoomId
        });
        
        // Find and update the reciprocal contact
        const reciprocalQuery = query(
          collection(db, 'contacts'),
          where('from', '==', otherUserEmail),
          where('to', '==', currentUser.email)
        );
        
        const reciprocalSnapshot = await getDocs(reciprocalQuery);
        if (!reciprocalSnapshot.empty) {
          await updateDoc(doc(db, 'contacts', reciprocalSnapshot.docs[0].id), {
            chatRoomId: chatRoomId
          });
        }
      }

      // Navigate to chat with the contact and chat room information
      navigate('/chat', { 
        state: { 
          contact: {
            email: otherUserEmail,
            chatRoomId,
            displayName: otherUserEmail.split('@')[0]
          }
        } 
      });
    } catch (error) {
      console.error('Error handling chat:', error);
      setError('Failed to open chat. Please try again.');
    }
  };

  const handleAcceptInvitation = async (contact) => {
    try {
      setLoading(true);
      
      // Create a chat room
      const chatRoomRef = await addDoc(collection(db, 'chatRooms'), {
        participants: [currentUser.email, contact.from],
        createdAt: serverTimestamp(),
        lastMessage: null,
        lastMessageTime: null
      });

      // Update the received contact status to active
      await updateDoc(doc(db, 'contacts', contact.id), {
        status: 'active',
        lastSeen: serverTimestamp(),
        online: true,
        chatRoomId: chatRoomRef.id
      });

      // Create a reciprocal contact
      await addDoc(collection(db, 'contacts'), {
        from: currentUser.email,
        to: contact.from,
        status: 'active',
        createdAt: serverTimestamp(),
        online: true,
        lastSeen: serverTimestamp(),
        blocked: false,
        muted: false,
        chatRoomId: chatRoomRef.id
      });

      // Find and update the original sender's contact
      const senderContactQuery = query(
        collection(db, 'contacts'),
        where('from', '==', contact.from),
        where('to', '==', currentUser.email)
      );
      
      const senderContactSnapshot = await getDocs(senderContactQuery);
      if (!senderContactSnapshot.empty) {
        await updateDoc(doc(db, 'contacts', senderContactSnapshot.docs[0].id), {
          status: 'active',
          chatRoomId: chatRoomRef.id
        });
      }

      setSuccess('Invitation accepted successfully!');
      setLoading(false);
    } catch (error) {
      console.error('Error accepting invitation:', error);
      setError('Failed to accept invitation. Please try again.');
      setLoading(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.contact-actions') && !event.target.closest('.user-avatar')) {
        setActiveDropdown(null);
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="contacts-container">
      <div className="contacts-header">
        <button className="back-button" onClick={handleBack}>
          <IoChevronBackOutline size={24} />
        </button>
        <h2>Contacts</h2>
        <div className="user-avatar" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <div className="avatar-initials">
            {getInitials(currentUser?.displayName || currentUser?.email)}
          </div>
          {isDropdownOpen && (
            <div className="user-dropdown">
              <div className="user-info">
                <div className="user-name">
                  {currentUser?.displayName || 'User'}
                </div>
                <div className="user-email">
                  {currentUser?.email}
                </div>
              </div>
              <div className="dropdown-options">
                <div className="dropdown-option" onClick={handleAccountClick}>
                  <IoPersonOutline size={20} />
                  Account
                </div>
                <div className="dropdown-option" onClick={handleLogout}>
                  <IoLogOutOutline size={20} />
                  Logout
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="search-bar">
        <i className="fas fa-search"></i>
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="contacts-content">
        {error && <div className="error-message">{error}</div>}
        {loading ? (
          <div className="loading-message">Loading...</div>
        ) : (
          <div className="contacts-list">
            {filteredContacts.length > 0 ? (
              filteredContacts.map(contact => {
                const contactInfo = getContactDisplayInfo(contact);
                return (
                  <div key={contact.id} className="contact-item">
                    <div className="contact-avatar">
                      <span className="avatar-initials">
                        {contactInfo.displayName.charAt(0).toUpperCase()}
                      </span>
                      {contactInfo.status === 'active' && contactInfo.online && (
                        <div className="online-status"></div>
                      )}
                    </div>
                    <div className="contact-info">
                      <div className="contact-name">{contactInfo.displayName}</div>
                      <div className="contact-status">
                        {contactInfo.status === 'pending' ? (
                          <span className="pending-status">
                            {contactInfo.isReceived ? 'Invitation Received' : 'Invitation Sent'}
                          </span>
                        ) : (
                          <div className="contact-email">{contactInfo.email}</div>
                        )}
                      </div>
                    </div>
                    <div className="contact-actions">
                      {contactInfo.status === 'active' ? (
                        <button className="action-button" onClick={() => handleChat(contact)}>
                          <i className="fas fa-comment"></i>
                        </button>
                      ) : contactInfo.isReceived && contactInfo.status === 'pending' ? (
                        <button 
                          className="action-button accept-invitation" 
                          onClick={() => handleAcceptInvitation(contact)}
                          disabled={loading}
                        >
                          <i className="fas fa-check"></i>
                        </button>
                      ) : null}
                      <button 
                        className="action-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(activeDropdown === contact.id ? null : contact.id);
                        }}
                      >
                        <i className="fas fa-ellipsis-v"></i>
                      </button>
                      {activeDropdown === contact.id && (
                        <div className="contact-options-dropdown">
                          {contactInfo.status === 'active' && (
                            <>
                              <div className="option-item" onClick={() => handleMuteContact(contact.id)}>
                                <i className="fas fa-bell-slash"></i>
                                {contactInfo.muted ? 'Unmute Notifications' : 'Mute Notifications'}
                              </div>
                              <div className="option-item" onClick={() => handleBlockContact(contact.id)}>
                                <i className="fas fa-ban"></i>
                                {contactInfo.blocked ? 'Unblock Contact' : 'Block Contact'}
                              </div>
                            </>
                          )}
                          <div className="option-item danger" onClick={() => handleDeleteContact(contact.id)}>
                            <i className="fas fa-trash"></i>
                            Delete {contactInfo.status === 'pending' ? 'Invitation' : 'Contact'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-contacts-container">
                <div className="no-contacts-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div className="add-contact-text">
                  Add New Contact
                </div>
                <div className="add-contact-icon" onClick={handleInviteClick}>
                  <i className="fas fa-user-plus"></i>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showInviteModal && (
        <div className="modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Invite Contact</h3>
              <button className="close-button" onClick={() => setShowInviteModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-content">
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <input
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button className="submit-button" onClick={handleInvite}>
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
